import {
  CalendarOutlined,
  DollarOutlined,
  RiseOutlined
} from "@ant-design/icons"
import { Col, Flex, Row, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import InputNumber from "../../../../components/Input/InputNumber.tsx"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { WorkspaceCardValue } from "../cards/WorkspaceCardValue.tsx"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { LeadOverviewField } from "./LeadOverviewField.tsx"
import {
  computeMargin,
  leadFinancialSnapshot
} from "./lead-overview-financials.ts"
import {
  type OverviewAccent,
  OverviewCardIcon,
  OverviewCardTitle
} from "./OverviewCardIcon.tsx"

const { Text } = Typography

/**
 * Purely decorative grouping — the value/empty-state logic still lives in
 * `LeadOverviewField`. `break-normal` undoes AntD's `word-break: break-word`,
 * which collapses the text's min-content width to a single character and lets a
 * squeezed tile wrap per letter instead of overflowing.
 */
function FinancialStatTile({
  icon,
  accent,
  label,
  value,
  subValue
}: {
  icon: ReactNode
  accent: OverviewAccent
  label: string
  value: string | null
  /** Omit entirely for a tile with no second line; pass `null` for "present but unset". */
  subValue?: string | null
}) {
  return (
    <Flex
      gap={"middle"}
      align={"center"}
      className={
        "rounded-card border border-border p-4 h-full [&_.ant-typography]:break-normal"
      }
    >
      <OverviewCardIcon icon={icon} accent={accent} size={40} />
      <Flex vertical gap={2} className={"min-w-0"}>
        <LeadOverviewField label={label} value={value} />
        {subValue !== undefined && (
          <WorkspaceCardValue value={subValue}>
            <Text type={"secondary"} className={"text-xs"}>
              {subValue}
            </Text>
          </WorkspaceCardValue>
        )}
      </Flex>
    </Flex>
  )
}

interface FinancialCardValues {
  sales_price: number | null
  planned_payable: number | null
}

interface LeadOverviewFinancialCardProps {
  lead: Lead
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadOverviewFinancialCard({
  lead,
  canEdit,
  onPatch
}: LeadOverviewFinancialCardProps) {
  const { t } = useTranslation()
  const snapshot = leadFinancialSnapshot(lead)

  const amount = (value: number | null) =>
    value == null ? null : budgetFormat(value)

  const values: FinancialCardValues = {
    sales_price: snapshot.salesPrice,
    planned_payable: snapshot.plannedPayable
  }

  return (
    <WorkspaceCard<FinancialCardValues>
      cardId={"financial"}
      title={
        <OverviewCardTitle icon={<DollarOutlined />} accent={"green"}>
          {t("leadWorkspace.cards.financial.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.financial.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={onPatch}
      renderEdit={({ values, setValues, saving }) => {
        const { margin } = computeMargin(
          values.sales_price,
          values.planned_payable
        )

        return (
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.financial.salesPrice")}
              >
                <InputNumber
                  autoFocus
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.financial.salesPrice")}
                  prefix={"$"}
                  placeholder={"0.00"}
                  step={0.01}
                  precision={2}
                  min={0}
                  maxIntegerDigits={10}
                  className={"w-full"}
                  value={values.sales_price ?? undefined}
                  onChange={(value) =>
                    setValues({ sales_price: value ?? null })
                  }
                />
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={8}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.financial.plannedPayable")}
              >
                <InputNumber
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.financial.plannedPayable")}
                  prefix={"$"}
                  placeholder={"0.00"}
                  step={0.01}
                  precision={2}
                  min={0}
                  maxIntegerDigits={10}
                  className={"w-full"}
                  value={values.planned_payable ?? undefined}
                  onChange={(value) =>
                    setValues({ planned_payable: value ?? null })
                  }
                />
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={8}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.financial.margin")}
              >
                <InputNumber
                  disabled
                  aria-label={t("leadWorkspace.cards.financial.margin")}
                  prefix={"$"}
                  precision={2}
                  className={"w-full"}
                  value={margin ?? undefined}
                />
              </LeadOverviewField>
            </Col>
          </Row>
        )
      }}
    >
      {/* A container query, not AntD's `Col`: its breakpoints read the
          viewport, and this card is only 4/9 of the overview grid. Tracks are
          sized for 90px of tile chrome plus ~114px of text, which holds every
          amount up to `-$99,999,999.99`; the wider end of the column's
          `max_digits=12` spills into the tile's own padding. */}
      <div className={"@container"}>
        <div
          data-testid={"lead-financial-tiles"}
          className={
            "grid grid-cols-1 gap-3 @min-[420px]:grid-cols-2 @min-[640px]:grid-cols-3"
          }
        >
          <FinancialStatTile
            icon={<DollarOutlined />}
            accent={"green"}
            label={t("leadWorkspace.cards.financial.salesPrice")}
            value={amount(snapshot.salesPrice)}
          />
          <FinancialStatTile
            icon={<CalendarOutlined />}
            accent={"orange"}
            label={t("leadWorkspace.cards.financial.plannedPayable")}
            value={amount(snapshot.plannedPayable)}
          />
          <FinancialStatTile
            icon={<RiseOutlined />}
            accent={"indigo"}
            label={t("leadWorkspace.cards.financial.margin")}
            value={amount(snapshot.margin)}
            subValue={
              snapshot.marginPercent == null
                ? undefined
                : `${snapshot.marginPercent.toFixed(1)}%`
            }
          />
        </div>
      </div>
    </WorkspaceCard>
  )
}

export { type FinancialCardValues, LeadOverviewFinancialCard }
